import { useMemo, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import { Booking } from "../../types";
import { X as XIcon } from "lucide-react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { formatCurrency } from "../../lib/utils";
import '../../assets/calendar.css';

interface CalendarTabProps {
    bookings: Booking[];
}

const getStatusColor = (status: string) => {
    switch (status) {
        case "confirmed":
            return "bg-green-500 hover:bg-green-600";
        case "completed":
            return "bg-blue-500 hover:bg-blue-600";
        case "cancelled":
            return "bg-red-500 hover:bg-red-600";
        default:
            return "bg-gray-500 hover:bg-gray-600";
    }
};

export default function CalendarTab({ bookings }: CalendarTabProps) {
    const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

    // Map of yyyy-mm-dd => bookings count
    const bookingDays = useMemo(() => {
        const map: Record<string, number> = {};
        bookings.forEach(b => {
            map[b.date] = (map[b.date] || 0) + 1;
        });
        return map;
    }, [bookings]);

    // Events for FullCalendar
    const events = useMemo(
        () =>
            bookings.map((b) => ({
                id: String(b.id),
                title: `${b.guests} Guest${b.guests > 1 ? "s" : ""} · ${
                    b.status === "cancelled"
                        ? "Cancelled ❌"
                        : b.status === "awaiting_restaurant_response"
                            ? "Awaiting"
                            : b.status.charAt(0).toUpperCase() + b.status.slice(1)
                }`,
                start: `${b.date}T${b.time}`,
                backgroundColor:
                    b.status === "confirmed"
                        ? "#22c55e"
                        : b.status === "completed"
                            ? "#2563eb"
                            : b.status === "cancelled"
                                ? "#dc2626"
                                : "#6b7280",
                borderColor:
                    b.status === "confirmed"
                        ? "#22c55e"
                        : b.status === "completed"
                            ? "#2563eb"
                            : b.status === "cancelled"
                                ? "#dc2626"
                                : "#6b7280",
                textColor: "#fff",
                extendedProps: { booking: b },
            })),
        [bookings]
    );



    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-2">
            <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                displayEventTime={false}
                headerToolbar={{
                    left: "prev,next today",
                    center: "title",
                    right: "dayGridMonth,timeGridWeek,timeGridDay",
                }}
                buttonText={{
                    today: "Today",
                    month: "Month",
                    week: "Week",
                    day: "Day",
                }}
                events={events}
                eventClick={(info) => {
                    setSelectedBooking(info.event.extendedProps.booking);
                }}
                height="auto"
                themeSystem="standard"
                dayMaxEventRows={3}
                slotMinTime="08:00:00"
                slotMaxTime="23:00:00"
                eventDisplay="block"
                displayEventEnd={false}
                nowIndicator
                noEventsContent={
                    <div className="text-gray-500 dark:text-gray-400 text-center py-8">
                        No bookings on this date.
                    </div>
                }
                dayCellContent={(arg) => {
                    const yyyyMMdd = arg.date.toLocaleDateString('en-CA');
                    const hasBooking = bookingDays[yyyyMMdd] > 0;
                    return (
                        <div className="flex items-center justify-center w-full h-full">
                            {hasBooking ? (
                                <span
                                    className="rounded-full bg-primary text-white w-8 h-8 flex items-center justify-center font-bold shadow"
                                    title={`${bookingDays[yyyyMMdd]} booking(s)`}
                                >
                        {arg.dayNumberText}
                    </span>
                            ) : (
                                <span className="w-8 h-8 flex items-center justify-center">
                        {arg.dayNumberText}
                    </span>
                            )}
                        </div>
                    );
                }}
            />

            {selectedBooking && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-25">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-sm w-full p-6 space-y-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                                Booking #{selectedBooking.id}
                            </h2>
                            <Button variant="ghost" size="icon" onClick={() => setSelectedBooking(null)}>
                                <XIcon className="h-5 w-5 text-gray-500 dark:text-gray-300" />
                            </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-gray-800 dark:text-gray-200">
                            <span className="font-medium">Date</span>
                            <span>{selectedBooking.date}</span>
                            <span className="font-medium">Time</span>
                            <span>{selectedBooking.time}</span>
                            <span className="font-medium">Guests</span>
                            <span>{selectedBooking.guests}</span>
                            <span className="font-medium">Total Price</span>
                            <span>{formatCurrency(selectedBooking.total_price)}</span>
                            <span className="font-medium">Status</span>
                            <span>
                                <Badge className={getStatusColor(selectedBooking.status)}>
                                    {selectedBooking.status.charAt(0).toUpperCase() +
                                        selectedBooking.status.slice(1)}
                                </Badge>
                            </span>
                            <span className="font-medium">Dietary Notes</span>
                            <span>{selectedBooking.dietary || "–"}</span>
                        </div>
                        <div className="flex justify-end">
                            <Button onClick={() => setSelectedBooking(null)}>Close</Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
